// src/lib/livret-editor-utils.js — Phase A.4 (PR éditeur admin livret, ADR-114)
//
// Helpers PURS pour l'éditeur admin drag-drop modules livret.
// Zéro dépendance externe. Testé Jest exhaustivement.
//
// Décisions actées (cf. plan-pr219 section 12) :
//   - Q-D6=A : conserver les 10 catégories DB existantes (CHECK constraint
//     ADR-108). UI affiche LIVRET_CATEGORY_LABELS humains FR uniquement.
//   - Q-D2=A : drag intra-catégorie uniquement. helper reorderInCategory
//     ne déplace JAMAIS un module hors de sa colonne.
//
// L-1 (pessimist) : LIVRET_CATEGORY_LABELS + LIVRET_CATEGORY_ORDER + CATEGORY_META
// sont la source UNIQUE pour le mapping catégorie → libellé humain / icône.
// PublicLivretPage doit importer CATEGORY_META d'ici (synchro garantie via tests).

// ── Labels humains catégories (FR) — exposés côté admin uniquement ──────
//
// Sprint A.9.4 PR-A93-2 (ADR-151) — Refonte architecture : 3 catégories finales
// pour livret_modules custom Dimitri. Catégories assistance/alentours/marches
// migrées vers livret_sections_auto IA. Catégories eco_responsable/partenaires/
// services_additionnels supprimées définitivement (décision UX simplification).
export const LIVRET_CATEGORY_LABELS = Object.freeze({
  informations: "Informations",
  guide_appareils: "Guide des appareils",
  autre: "Autre",
});

// ── Ordre d'affichage colonnes éditeur admin ────────────────────────────
export const LIVRET_CATEGORY_ORDER = Object.freeze([
  "informations",
  "guide_appareils",
  "autre",
]);

// ── CATEGORY_META — source unique label + icône ─────────────────────────
//
// L-1 (pessimist) : exporté pour PublicLivretPage (voyageur). Synchro tests.
export const CATEGORY_META = Object.freeze({
  informations: { label: "Informations", icon: "📋" },
  guide_appareils: { label: "Guide des appareils", icon: "🔌" },
  autre: { label: "Autre", icon: "📄" },
});

// ── Sous-catégories alentours ───────────────────────────────────────────

export const LIVRET_ALENTOURS_SUBCATEGORY_LABELS = Object.freeze({
  culture: "Culture",
  commerces: "Commerces",
  nature: "Nature",
  restaurants: "Restaurants",
  sorties: "Sorties",
  points_cles: "Points clés",
});

// ── Helpers purs ────────────────────────────────────────────────────────

/**
 * Groupe une liste de modules par catégorie. Conserve l'ordre relatif initial
 * (typiquement déjà trié par order_index côté API).
 *
 * ADR-171 (F-CRIT-1 pessimist) : accepte un 2e paramètre `categoryOrder`
 * optionnel pour supporter les catégories custom dynamiques (PR-FIX-5d).
 * Si absent ou vide, fallback sur LIVRET_CATEGORY_ORDER (rétrocompat).
 *
 * @param {Array<{category:string, ...}>} modules
 * @param {Array<string>} [categoryOrder] - liste dynamique slugs (ADR-171)
 * @returns {Object<string, Array>} map { category: modules[] }
 */
export function groupByCategory(modules, categoryOrder) {
  const order =
    Array.isArray(categoryOrder) && categoryOrder.length > 0
      ? categoryOrder
      : LIVRET_CATEGORY_ORDER;
  const map = {};
  for (const cat of order) {
    map[cat] = [];
  }
  if (!Array.isArray(modules)) return map;
  for (const m of modules) {
    if (!m || typeof m.category !== "string") continue;
    if (!map[m.category]) {
      // Catégorie inconnue (hors `order`) → ignorée silencieusement.
      continue;
    }
    map[m.category].push(m);
  }
  return map;
}

/**
 * Calcule le nouvel ordre des IDs après drag-drop intra-catégorie.
 * NE déplace JAMAIS un module hors de sa catégorie d'origine (Q-D2=A).
 *
 * @param {Array<{id:string, category:string}>} modules - liste complète modules
 * @param {string} category - catégorie cible
 * @param {string} activeId - id module en cours de drag
 * @param {string} overId - id module au-dessus duquel on lâche
 * @returns {Array<string>} new ordered_ids pour la catégorie (ou null si no-op)
 */
export function reorderInCategory(modules, category, activeId, overId) {
  if (!Array.isArray(modules) || !category) return null;
  if (!activeId || !overId || activeId === overId) return null;

  const inCategory = modules.filter((m) => m && m.category === category);
  const oldIdx = inCategory.findIndex((m) => m.id === activeId);
  const newIdx = inCategory.findIndex((m) => m.id === overId);
  if (oldIdx === -1 || newIdx === -1) return null;
  // arrayMove inline (pas de dépendance externe)
  const reordered = [...inCategory];
  const [moved] = reordered.splice(oldIdx, 1);
  reordered.splice(newIdx, 0, moved);
  return reordered.map((m) => m.id);
}

/**
 * Construit l'URL preview voyageur depuis un token UUID.
 *
 * @param {string|null} token - token UUID v4 form_tokens type='livret'
 * @returns {string|null} URL absolue /livret/r/:token ou null si token invalide
 */
export function buildLivretPreviewUrl(token) {
  if (!token || typeof token !== "string") return null;
  const trimmed = token.trim();
  // Validation lâche format UUID (la migration côté serveur valide strict)
  if (!/^[0-9a-f-]{36}$/i.test(trimmed)) return null;
  return `/livret/r/${trimmed}`;
}

/**
 * Retourne le label humain d'une catégorie. Fallback = catégorie brute.
 *
 * @param {string} category
 * @returns {string}
 */
export function getCategoryLabel(category) {
  return LIVRET_CATEGORY_LABELS[category] || category;
}

/**
 * Retourne l'icône emoji d'une catégorie. Fallback = '📄'.
 *
 * @param {string} category
 * @returns {string}
 */
export function getCategoryIcon(category) {
  return CATEGORY_META[category]?.icon || "📄";
}
